import React, { useEffect, useRef, useState } from "react";
import { Button, ListGroup, Modal, ModalBody, ModalFooter, ModalHeader, ProgressBar } from "react-bootstrap";
import buildVer from "../ver.json";

interface VerJson {
    ver: string;
    description: string[];
}

const BUILD_VER = buildVer.ver;

export default function AppUpdater() {
    const [open, setOpen] = useState(false);
    const [showFullHistory, setShowFullHistory] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [serverVer, setServerVer] = useState<string | null>(null);
    const [description, setDescription] = useState<string[]>([]);
    const timerRef = useRef<number | null>(null);
    const swReadyRef = useRef(false);

    const showUpdateDialog = (ver: string, desc: string[]) => {
        setServerVer(ver);
        setDescription(desc);
        setOpen(true);
    };

    const checkForUpdate = async () => {
        try {
            const response = await fetch('/ver.json?t=' + Date.now(), {
                cache: 'no-cache',
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                }
            });

            if (!response.ok) {
                return;
            }

            const data: VerJson = await response.json();

            if (data.ver !== BUILD_VER) {
                console.log('Обновление доступно:', data.ver);

                // Принудительно проверяем наличие нового SW
                if ('serviceWorker' in navigator) {
                    const reg = await navigator.serviceWorker.ready;
                    reg.update().catch(() => {});
                }

                // Если SW уже сообщил о готовности, показываем диалог сразу
                if (swReadyRef.current) {
                    showUpdateDialog(data.ver, data.description || []);
                }
                // Иначе ждем onNeedRefresh — он вызовет showUpdateDialog
            }
        } catch (error) {
            console.debug('Проверка обновления не удалась:', error);
        }
    };

    useEffect(() => {
        // onNeedRefresh вызывается из main.tsx когда новый SW загрузился и готов к активации
        const handleNeedRefresh = () => {
            console.log('Новый SW готов к активации');
            swReadyRef.current = true;

            // Показываем диалог если версия уже известна
            if (serverVer) {
                setOpen(true);
            }
        };

        window.onNeedRefresh = handleNeedRefresh;

        // Первая проверка ver.json при монтировании
        checkForUpdate();

        // Периодическая проверка каждую 1 минуту
        timerRef.current = window.setInterval(checkForUpdate, 1 * 60 * 1000);

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [serverVer]);

    const handleUpdate = async () => {
        setUpdating(true);

        if (window.__updateSW) {
            // Активируем новый SW и перезагружаем страницу
            await window.__updateSW(true);
        } else {
            window.location.reload();
        }
    };

    return (
        <Modal show={open} onHide={() => setOpen(false)} size="lg">
            <Modal.Header closeButton>
                <h5 className="mb-0">Доступно обновление!</h5>
            </Modal.Header>
            <ModalBody>
                <p className="text-muted mb-3">
                    Доступна новая версия приложения: <strong>{serverVer}</strong> (текущая: {BUILD_VER})
                </p>

                {description.length > 0 && (
                    <>
                        <ListGroup variant="flush">
                            {(showFullHistory ? description : description.slice(0, 3)).map((item, index) => (
                                <ListGroup.Item key={index} className="py-2">
                                    {item}
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                        {description.length > 3 && (
                            <Button
                                variant="link"
                                className="mt-2 p-0"
                                onClick={() => setShowFullHistory(!showFullHistory)}
                            >
                                {showFullHistory ? 'Скрыть' : 'Показать всю историю'}
                            </Button>
                        )}
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                {updating ? (
                    <div className="w-100">
                        <div className="d-flex justify-content-between mb-2">
                            <span>Идет обновление...</span>
                        </div>
                        <ProgressBar animated now={100} />
                    </div>
                ) : (
                    <>
                        <Button variant="success" onClick={handleUpdate}>
                            Обновить
                        </Button>{" "}
                        <Button variant="outline-secondary" onClick={() => setOpen(false)}>
                            Позже
                        </Button>
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
}